import Mapbox from 'mapbox-gl';
import { layerSources, layers } from './layers';
import { getRuteCykelstier, getFarligeKrydsCloseToGeometry } from '@/api/osm';
import polyline from '@mapbox/polyline';
import distance from '@turf/distance';
import pointToLineDistance from '@turf/point-to-line-distance';

export function createMap(container) {
  return new Mapbox.Map({
    container,
    style: `https://api.maptiler.com/maps/e8e95780-9829-4d49-9df3-5f99708e5585/style.json?key=SsrA3lIs6XcvTddgU10h`,
    center: [8.97, 56.15],
    zoom: 11,
    maxBounds: [[6.19, 54], [16.6, 59]],
    minZoom: 6,
  });
}
function getItineraryLegChanges(itinerary) {
  const data = {
    type: 'FeatureCollection',
    features: [],
  };
  const { legs } = itinerary;
  data.features = legs.map(leg => {
    const { mode, from } = leg;
    const { lat, lon, name } = from;

    return {
      type: 'Feature',
      properties: {
        mode,
        lat,
        lon,
        name,
      },
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
    };
  });
  return data;
}

export function getItineraryBusStops(itinerary) {
  const data = {
    type: 'FeatureCollection',
    features: [],
  };
  const busLegs = itinerary.legs.filter(leg => leg.mode === 'BUS');
  let busStops = [];
  busLegs.forEach(leg => {
    busStops.push({ ...leg.from, endPoint: 'true' });
    leg.intermediateStops.forEach(stop => {
      busStops.push({ ...stop, endPoint: 'false' });
    });
    busStops.push({ ...leg.to, endPoint: 'true' });
  });
  data.features = busStops.map((stop, i) => {
    const {
      arrival,
      departure,
      lat,
      lon,
      name,
      stopId,
      stopIndex,
      stopSequence,
      vertexType,
      endPoint,
    } = stop;
    return {
      type: 'Feature',
      properties: {
        arrival,
        departure,
        lat,
        lon,
        name,
        stopId,
        stopIndex,
        stopSequence,
        vertexType,
        endPoint,
      },
      geometry: {
        type: 'Point',
        coordinates: [lon, lat],
      },
    };
  });
  return data;
}

/**
 * Add farligKryds to map and to closest step in itinerary
 * @param {Object} farligKryds - The point feature of the dangerous intersection
 * @param {object} itinerary - To add map feature to
 * @param {object} leg - To find closest step to add farlig kryds information to
})
 */
function addFarligKryds(farligKryds, itinerary, leg) {
  // const isClose =
  //   pointToLineDistance(farligKryds.geometry.coordinates, geometry, {
  //     units: 'meters',
  //   }) < 20;
  // if (isClose) {

  // Traverser steps for at se om farlig kryds er ved et af stegene
  // kan være et kryds uden steg, f ex hvor man bare passerer og går ret frem
  let closestDistance = 20;
  let closestStepIndex = -1;
  leg.steps.forEach((step, i) => {
    const stepToKrydsDistance =
      distance(farligKryds.geometry.coordinates, [step.lon, step.lat]) * 1000; // kilometers to meters
    if (stepToKrydsDistance < closestDistance) {
      closestStepIndex = i;
      closestDistance = stepToKrydsDistance;
    }
  });

  // Hvis det ikke er ved et steg, lag linjer mellem hvert steg (punkter)
  // og find den der er nærmest krydset. Advarselen kommer så på det første
  // af stegene
  if (closestStepIndex === -1) {
    let stepCoordinates = leg.steps.map(step => {
      return [step.lon, step.lat];
    });
    stepCoordinates.push([leg.to.lon, leg.to.lat]);
    const stepLineSegments = stepCoordinates.reduce((acc, curr, i) => {
      if (i > 0) {
        acc.push([stepCoordinates[i - 1], curr]);
      }
      return acc;
    }, []);
    closestDistance = 100;
    stepLineSegments.forEach((segment, i) => {
      const segmentToKrydsDistance = pointToLineDistance(
        farligKryds.geometry.coordinates,
        segment,
        { units: 'meters' }
      );
      if (segmentToKrydsDistance < closestDistance) {
        closestDistance = segmentToKrydsDistance;
        closestStepIndex = i;
      }
    });
  }
  if (closestStepIndex > -1) {
    // Skal kun vise advarsel for cykel
    if (leg.mode === 'BICYCLE') {
      // Til visning i kort:
      itinerary.advarselData.features.push(farligKryds);

      // Til visning i liste:
      if (
        !leg.steps[closestStepIndex].farligeKryds ||
        leg.steps[closestStepIndex].farligeKryds.findIndex(
          a => a.properties.uheldssted === farligKryds.properties.uheldssted
        ) < 0
      ) {
        leg.steps[closestStepIndex].farligeKryds
          ? leg.steps[closestStepIndex].farligeKryds.push(farligKryds)
          : (leg.steps[closestStepIndex].farligeKryds = [farligKryds]);
      }
    }
  }
  // }
}

/**
 * Prepare mapData for selected itinerary. Does the following:
 * - Converts the otp-geometry, which is an encoded string, to GeoJSON. Set as property "mapData" on the itinerary.
 * - Calculates the bbox of the geometry and sets it as a property "bounds" on the itinerary.
 * @param {Object} itinerary - The itinerary to prepare mapData for
 * @param {object} state - the current state, used to find wfs data for "dangerous intersections"
})
 */
function prepareItineraryMapData(itinerary, map) {
  itinerary.mapData = {
    type: 'FeatureCollection',
    features: itinerary.legs.map(leg => {
      const geometry = polyline.toGeoJSON(leg.legGeometry.points);
      geometry.coordinates.forEach(coor => {
        itinerary.bounds.extend(coor);
      });
      getFarligeKrydsCloseToGeometry(geometry).then(features => {
        features.forEach(feature => {
          addFarligKryds(feature, itinerary, leg);
        });
        map.getSource(layerSources.advarsel).setData(itinerary.advarselData);
      });
      return {
        type: 'Feature',
        properties: {
          mode: leg.mode,
        },
        geometry,
      };
    }),
  };
}

export function setMapItinerary(map, itinerary, selectedItineraryIndex) {
  this.map.getSource(layerSources.advarsel).setData({
    type: 'FeatureCollection',
    features: [],
  });
  itinerary.legs.forEach(leg => {
    const geometry = polyline.toGeoJSON(leg.legGeometry.points);
    if (leg.mode === 'BICYCLE') {
      getRuteCykelstier(geometry)
        .then(resultat => {
          leg.bikelaneLength = 0;
          if (resultat.features) {
            resultat.features.forEach(feature => {
              itinerary.cykelstier.features.push(feature);
              leg.bikelaneLength += feature.properties.len;
            });
            // tjeker at vi stadig har valgt denne cykelruten
            if (this.selectedItineraryIndex === selectedItineraryIndex) {
              map
                .getSource(layerSources.cykelsti)
                .setData(itinerary.cykelstier);
            }
          }
          // console.log('Cykelstiens totallængde: ' + leg.bikelaneLength);
        })
        .catch(error => {
          console.log('Kunne ikke hente cykelstier: ' + error.message);
        });
    }
  });
  if (!itinerary.mapData) {
    prepareItineraryMapData(itinerary, map);
  } else {
    map.getSource(layerSources.advarsel).setData(itinerary.advarselData);
  }
  map.getSource(layerSources.itinerary).setData(itinerary.mapData);
  map.getSource(layerSources.cykelsti).setData(itinerary.cykelstier);
  map.getSource(layerSources.busStops).setData(getItineraryBusStops(itinerary));
  map
    .getSource(layerSources.legChange)
    .setData(getItineraryLegChanges(itinerary));
  map.fitBounds(itinerary.bounds, {
    padding: 100,
  });
  // }
}

const showStepQueue = [];
export function showStep(map, step) {
  showStepQueue.push(1);
  const { lat, lon } = step;
  map.getSource(layerSources.stepClick).setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      },
    ],
  });
  map.setPaintProperty(layers.stepClick, 'circle-opacity', 1);
  setTimeout(() => {
    showStepQueue.pop();
    if (showStepQueue.length === 0) {
      map.setPaintProperty(layers.stepClick, 'circle-opacity', 0);
      setTimeout(() => {
        if (showStepQueue.length === 0) {
          map.getSource(layerSources.stepClick).setData({
            type: 'FeatureCollection',
            features: [],
          });
        }
        map.setPaintProperty(layers.stepClick, 'circle-opacity', 1);
      }, 2000);
    }
  }, 2000);
  map.flyTo({
    center: { lat, lon },
    zoom: 15,
  });
}
